import { z } from 'zod'
import { FRENTE_KEYS, type FrenteKey } from '../../contract'

/* ERP · experiência Staff (a do colaborador): o contrato da API /api/v1/admin/erp/staff (server/erp/staff.ts).
   Nada aqui é dado de exemplo: começa vazio. O que vem pronto é só o MODELO da trilha de 90 dias (passos e prazos
   em dias desde a admissão), os temas do pulso, os momentos de verdade e as trilhas da Academia CFB Cognitive.

   GET   /staff/me                         -> StaffMe          (qualquer perfil do Staff: a própria jornada, o PDI e
                                                                 se já respondeu o pulso do mês)
   PATCH /staff/me          StaffSelfPatch -> StaffPerson      (passos feitos, momentos, PDI da própria pessoa)
   GET   /staff/people                     -> StaffPerson[]    (só lideranças: admin e gerência)
   GET   /staff/people/:email              -> StaffPerson      (só lideranças; sem registro = o modelo, stored false)
   PATCH /staff/people/:email StaffLeadPatch -> StaffPerson    (só lideranças: admissão, função, frente, buddy, PDI…)
   POST  /staff/pulse       PulseInput     -> PulseStatus      (uma resposta por pessoa por mês; 409 conflict se já
                                                                 respondeu. A resposta não guarda quem respondeu)
   GET   /staff/pulse                      -> PulseSummary     (só lideranças; agregado por frente, só com n >= 3)
   GET   /staff/knowledge                  -> KnowledgeProcess[] (qualquer perfil do Staff)
   POST  /staff/knowledge   KnowledgeInput -> KnowledgeProcess (só lideranças)
   PATCH /staff/knowledge/:id KnowledgePatch -> KnowledgeProcess (lideranças; dono e backup mudam estado e revisão)
   POST  /staff/knowledge/:id/request      -> KnowledgeProcess (qualquer perfil: pede a documentação; vai à auditoria) */

// ---------- quem lidera ----------

/** Perfis que veem a aba Equipe e cuidam da jornada das pessoas. */
export const STAFF_LEAD_ROLES = ['admin', 'gerencia'] as const
export const isStaffLead = (role: string | null | undefined) => (STAFF_LEAD_ROLES as readonly string[]).includes(role ?? '')

// ---------- campos ----------

const validDate = (v: string) => !Number.isNaN(Date.parse(`${v}T12:00:00Z`)) && new Date(`${v}T12:00:00Z`).toISOString().slice(0, 10) === v
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(validDate)
export const staffEmailSchema = z.string().trim().toLowerCase().email().max(160)
const slugSchema = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,47}$/)
const progressSchema = z.number().int().min(0).max(100)
const frenteSchema = z.enum(FRENTE_KEYS)

// ---------- trilha de 90 dias (modelo) ----------

export type OnboardingTemplateStep = { key: string; label: string; dueDays: number; detail: string; source: string | null }

/** Primeira entrega relevante sugerida por frente (a liderança combina a de cada pessoa). */
export const FIRST_DELIVERY: Record<FrenteKey, string> = {
    relacionamento: 'atender sozinha(o) uma fila de boas-vindas de padrinhos',
    captacao: 'cadastrar os padrinhos de um evento em menos de 24 h',
    corporativo: 'montar o relatório de impacto de uma empresa parceira',
    marketing: 'publicar uma história de impacto com uso de imagem aprovado',
    programas: 'acompanhar a prestação de contas mensal de uma OSP',
    financeiro: 'fechar uma conciliação de recorrência do mês',
    gestao: 'conduzir a revisão mensal de indicadores de uma experiência',
    tecnologia: 'publicar uma automação com supervisão humana',
}

/** Modelo da trilha (o mesmo para todas as frentes; só a primeira entrega muda). Prazo = dias desde a admissão. */
export function onboardingTemplate(frente: FrenteKey | null): OnboardingTemplateStep[] {
    return [
        { key: 'acessos', dueDays: 1, label: 'Acessos prontos', detail: 'E-mail, telas da frente no ERP e verificação em duas etapas.', source: 'IT Acceptable Use Procedure' },
        { key: 'salvaguarda', dueDays: 7, label: 'Formação em salvaguarda infantil', detail: 'Política de Proteção e Protocolo Comportamental na integração. Preocupação se relata em até 24 h.', source: 'PRO-PRO-080 §5.1.7.1 · PRO-POL-080 §2.9' },
        { key: 'lgpd', dueDays: 14, label: 'LGPD e segurança da informação', detail: 'Dados pessoais de padrinhos e crianças: o que a sua frente pode ver e como pedir acesso.', source: 'PRO GPG-ECP-088 §12.1' },
        { key: 'buddy', dueDays: 30, label: 'Conversas com o buddy', detail: 'Uma pessoa da frente acompanha o primeiro mês, com conversas semanais.', source: null },
        { key: 'visita', dueDays: 45, label: 'Visita a uma OSP', detail: 'Com a equipe de Programas, conhecer uma organização parceira e o território.', source: null },
        { key: 'entrega', dueDays: 90, label: 'Primeira entrega relevante', detail: frente ? `Por exemplo: ${FIRST_DELIVERY[frente]}.` : 'Uma entrega combinada com a liderança.', source: null },
    ]
}

export const ONBOARDING_DAYS = 90

// ---------- momentos de verdade, trilhas, pulso, conhecimento ----------

export const MOMENT_KINDS = ['primeiro_contato', 'primeiro_dia', 'primeira_visita', 'primeira_entrega', 'mudanca_funcao', 'saida'] as const
export type MomentKind = (typeof MOMENT_KINDS)[number]
export const MOMENT_LABELS: Record<MomentKind, string> = {
    primeiro_contato: 'Primeiro contato com a vaga',
    primeiro_dia: 'Primeiro dia',
    primeira_visita: 'Primeira visita ao território',
    primeira_entrega: 'Primeira entrega relevante',
    mudanca_funcao: 'Promoção ou mudança de função',
    saida: 'Saída',
}

/** Trilhas da Academia CFB Cognitive (progresso 0–100 informado pela pessoa ou pela liderança). */
export const TRACK_KEYS = ['dados', 'ia', 'experiencia'] as const
export type TrackKey = (typeof TRACK_KEYS)[number]
export const TRACK_LABELS: Record<TrackKey, string> = { dados: 'Dados no dia a dia', ia: 'IA responsável', experiencia: 'Experiência do stakeholder' }

/** Temas fechados (texto livre poderia identificar quem respondeu). */
export const PULSE_THEMES = ['carga', 'ferramentas', 'reconhecimento', 'desenvolvimento', 'lideranca', 'proposito', 'bem_estar', 'comunicacao'] as const
export type PulseTheme = (typeof PULSE_THEMES)[number]
export const PULSE_THEME_LABELS: Record<PulseTheme, string> = {
    carga: 'Carga de trabalho',
    ferramentas: 'Ferramentas e sistemas',
    reconhecimento: 'Reconhecimento',
    desenvolvimento: 'Desenvolvimento',
    lideranca: 'Liderança',
    proposito: 'Propósito',
    bem_estar: 'Bem-estar',
    comunicacao: 'Comunicação',
}
/** Abaixo disso a frente não aparece (nem contagem): ninguém é identificável. */
export const PULSE_MIN_N = 3

export const KNOWLEDGE_STATUSES = ['documentado', 'rascunho', 'so_uma_pessoa'] as const
export type KnowledgeStatus = (typeof KNOWLEDGE_STATUSES)[number]
export const KNOWLEDGE_STATUS_LABELS: Record<KnowledgeStatus, string> = {
    documentado: 'Documentado',
    rascunho: 'Em rascunho',
    so_uma_pessoa: 'Só uma pessoa sabe',
}

// ---------- pessoa: jornada + PDI ----------

export const staffStepSchema = z.object({
    key: slugSchema,
    label: z.string(),
    detail: z.string().nullable(),
    source: z.string().nullable(),
    due: isoDateSchema.nullable(),
    doneAt: z.string().nullable(),
})
export type StaffStep = z.infer<typeof staffStepSchema>

export const momentSchema = z.object({ kind: z.enum(MOMENT_KINDS), date: isoDateSchema })
export type StaffMoment = z.infer<typeof momentSchema>

export const goalSchema = z.object({
    id: slugSchema,
    title: z.string().trim().min(2).max(140),
    progress: progressSchema,
    reviewDate: isoDateSchema.nullable(),
})
export type PdiGoal = z.infer<typeof goalSchema>

export const tracksSchema = z.object({ dados: progressSchema.nullable(), ia: progressSchema.nullable(), experiencia: progressSchema.nullable() })
export type TrackProgress = z.infer<typeof tracksSchema>

export type StaffPerson = {
    email: string
    frente: FrenteKey | null
    function: string | null
    admissionDate: string | null
    buddyEmail: string | null
    steps: StaffStep[]
    moments: StaffMoment[]
    pdi: { goals: PdiGoal[]; mentorEmail: string | null; tracks: TrackProgress }
    /** false = ainda sem registro: o que aparece é o modelo. */
    stored: boolean
    updatedAt: string | null
    updatedBy: string | null
}

/** O que fica guardado: os passos só como "feito em" por chave (rótulo e prazo saem do modelo + admissão). */
export type StaffPersonRecord = Omit<StaffPerson, 'steps' | 'stored'> & { stepsDone: Record<string, string> }

export const staffSelfPatchSchema = z.object({
    steps: z.array(z.object({ key: slugSchema, done: z.boolean() })).max(20).optional(),
    moments: z.array(momentSchema).max(30).optional(),
    pdi: z.object({
        goals: z.array(goalSchema).max(10).optional(),
        mentorEmail: staffEmailSchema.nullable().optional(),
        tracks: tracksSchema.partial().optional(),
    }).strict().optional(),
}).strict()
export type StaffSelfPatch = z.infer<typeof staffSelfPatchSchema>

export const staffLeadPatchSchema = staffSelfPatchSchema.extend({
    admissionDate: isoDateSchema.nullable().optional(),
    function: z.string().trim().min(2).max(80).nullable().optional(),
    frente: frenteSchema.nullable().optional(),
    buddyEmail: staffEmailSchema.nullable().optional(),
}).strict()
export type StaffLeadPatch = z.infer<typeof staffLeadPatchSchema>

// ---------- pulso ----------

export const pulseInputSchema = z.object({
    score: z.number().int().min(0).max(10),
    theme: z.enum(PULSE_THEMES).nullable().optional(),
    /** A frente em que a pessoa trabalha (precisa estar entre as do perfil). */
    frente: frenteSchema,
}).strict()
export type PulseInput = z.infer<typeof pulseInputSchema>

/** Uma resposta guardada: sem e-mail, sem data, só o mês. */
export type PulseAnswer = { month: string; frente: FrenteKey; score: number; theme: PulseTheme | null }

export type PulseStatus = { month: string; answered: boolean }

export type PulseGroup = {
    /** 'all' = a organização inteira. */
    frente: FrenteKey | 'all'
    /** false = menos de PULSE_MIN_N respostas: nada aparece (nem a contagem). */
    enough: boolean
    count: number | null
    enps: number | null
    /** eNPS deste mês menos o do mês anterior (só se os dois tiverem n suficiente). */
    trend: number | null
    themes: { theme: PulseTheme; count: number }[]
}

export type PulseSummary = { month: string; previousMonth: string; minN: number; org: PulseGroup; frentes: PulseGroup[] }

export type StaffMe = { person: StaffPerson; lead: boolean; pulse: PulseStatus }

// ---------- inventário de conhecimento ----------

export const knowledgeInputSchema = z.object({
    name: z.string().trim().min(3).max(140),
    frente: frenteSchema,
    ownerEmail: staffEmailSchema.nullable().optional(),
    backupEmail: staffEmailSchema.nullable().optional(),
    status: z.enum(KNOWLEDGE_STATUSES),
    reviewedAt: isoDateSchema.nullable().optional(),
    /** Id do documento na memória do ChildFund (o playbook). */
    playbookSourceId: z.string().trim().min(1).max(200).nullable().optional(),
}).strict()
export type KnowledgeInput = z.infer<typeof knowledgeInputSchema>

export const knowledgePatchSchema = knowledgeInputSchema.partial().strict()
export type KnowledgePatch = z.infer<typeof knowledgePatchSchema>

/** O que dono e backup (sem ser liderança) podem mudar. */
export const KNOWLEDGE_OWNER_FIELDS = ['status', 'reviewedAt', 'playbookSourceId', 'backupEmail'] as const satisfies readonly (keyof KnowledgePatch)[]

export type KnowledgeProcess = {
    id: string
    name: string
    frente: FrenteKey
    ownerEmail: string | null
    backupEmail: string | null
    status: KnowledgeStatus
    reviewedAt: string | null
    playbookSourceId: string | null
    /** Último pedido de documentação (o pedido em si fica na auditoria). */
    docRequestedAt: string | null
    updatedAt: string
    updatedBy: string
}
